export function splitEvolutionTiers(evolutionTiers) {
  if (!evolutionTiers || typeof evolutionTiers !== "string") return [];
  return evolutionTiers
    .split(";")
    .map((tier) => tier.trim())
    .filter((tier) => tier.length > 0);
}

function normalizeWhitespace(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function normalizeStandardTierSuffix(suffix) {
  return normalizeWhitespace(suffix).toUpperCase();
}

export function normalizeTierKey(tier) {
  const trimmedTier = normalizeWhitespace(tier);
  if (!trimmedTier) return "";

  const withoutPrefix = trimmedTier.replace(/^tier\s+/i, "");
  const lowerTier = withoutPrefix.toLowerCase();

  if (lowerTier === "standard") return "Standard";
  if (lowerTier === "recruit") return "Recruit";

  if (lowerTier.startsWith("standard ")) {
    const suffix = normalizeStandardTierSuffix(withoutPrefix.slice("standard".length));
    return suffix ? `Tier Standard ${suffix}` : "Standard";
  }

  return `Tier ${withoutPrefix.toUpperCase()}`;
}

export function collapseTierLabel(tier) {
  const exactTier = normalizeTierKey(tier);
  if (!exactTier) return "";
  if (exactTier === "Standard" || exactTier === "Tier Standard WK") return "Standard";
  if (exactTier === "Recruit") return "Recruit";
  if (!exactTier.startsWith("Tier ")) return exactTier;

  const remainder = exactTier.slice(5).trim();
  const firstToken = remainder.split(" ")[0];

  if (!firstToken || firstToken.toLowerCase() === "standard") {
    return "Standard";
  }

  return `Tier ${firstToken}`;
}

function getExactBaseTier(label) {
  const normalizedLabel = normalizeWhitespace(label);
  if (!normalizedLabel) return "";
  if (normalizedLabel === "Standard") return "Standard";
  if (normalizedLabel === "Recruit") return "Recruit";
  return normalizeTierKey(normalizedLabel);
}

function uniqueEntries(entries) {
  const seen = new Set();
  return entries.filter((entry) => {
    if (!entry.exactTier) return false;
    if (seen.has(entry.exactTier)) return false;
    seen.add(entry.exactTier);
    return true;
  });
}

export function resolveExactTierForLabel(label, strategyExactTiers = [], userExactTiers = []) {
  const baseExactTier = getExactBaseTier(label);
  const strategySet = new Set(strategyExactTiers);
  const userSet = new Set(userExactTiers);

  if (baseExactTier && strategySet.has(baseExactTier) && userSet.has(baseExactTier)) {
    return baseExactTier;
  }

  const exactUserMatch = userExactTiers.find((exactTier) => strategySet.has(exactTier));
  if (exactUserMatch) {
    return exactUserMatch;
  }

  if (baseExactTier && strategySet.has(baseExactTier)) {
    return baseExactTier;
  }

  return strategyExactTiers[0] || "";
}

export function buildManagementFeeTierOptions(
  strategyTiers = [],
  evolutionTiers = "",
  preservedSelection = "",
) {
  const strategyEntries = uniqueEntries(
    strategyTiers.map((tier) => {
      const exactTier = normalizeTierKey(tier);
      return {
        exactTier,
        label: collapseTierLabel(exactTier),
      };
    })
  );

  const strategyGroups = new Map();
  strategyEntries.forEach((entry) => {
    if (!strategyGroups.has(entry.label)) {
      strategyGroups.set(entry.label, []);
    }
    strategyGroups.get(entry.label).push(entry.exactTier);
  });

  const userEntries = uniqueEntries(
    splitEvolutionTiers(evolutionTiers).map((tier) => {
      const exactTier = normalizeTierKey(tier);
      return {
        exactTier,
        label: collapseTierLabel(exactTier),
      };
    })
  );

  const userGroups = new Map();
  userEntries.forEach((entry) => {
    if (!userGroups.has(entry.label)) {
      userGroups.set(entry.label, []);
    }
    userGroups.get(entry.label).push(entry.exactTier);
  });

  const hasUserFilter = userEntries.length > 0;
  const preservedExactTier = normalizeTierKey(preservedSelection);
  const preservedLabel = collapseTierLabel(preservedExactTier);
  const options = [];

  for (const [label, strategyExactTiers] of strategyGroups.entries()) {
    const userExactTiers = userGroups.get(label) || [];
    const preserveSavedTier =
      label === preservedLabel && strategyExactTiers.length > 0;
    if (hasUserFilter) {
      if (userExactTiers.length === 0 && !preserveSavedTier) {
        continue;
      }
    } else if (label !== "Standard" && !preserveSavedTier) {
      // No EvolutionTiers configured for the user — only the Standard tier
      // should be offered, never the full strategy tier list.
      continue;
    }

    const exactTier = resolveExactTierForLabel(
      label,
      strategyExactTiers,
      preserveSavedTier
        ? [preservedExactTier, ...userExactTiers]
        : userExactTiers,
    );
    if (!exactTier) continue;

    options.push({
      label,
      exactTier,
    });
  }

  return options;
}

export function resolveManagementFeeTierSelection(selection, options = []) {
  const normalizedSelection = collapseTierLabel(selection);
  if (!normalizedSelection) {
    return { label: "", exactTier: "" };
  }

  const match = options.find((option) => option.label === normalizedSelection);
  if (!match) {
    return { label: "", exactTier: "" };
  }

  return {
    label: match.label,
    exactTier: match.exactTier,
  };
}
